/**
 * The parcel the label is quoted on: the units it is measured in, its dimensions, its total weight
 * and the weight of every returned line inside it. Seeded from the prefetch and edited in place; the
 * quote and the approve take it back as parcelRequest and itemsRequest.
 *
 * Held as it was read and posted back the same way: the backend reads the body with the serializer
 * that wrote the prefetch, so the parcel and the items go back in the carrier's own keys, every
 * field of a line included. A card the merchant never opened goes back exactly as it came.
 *
 * The units belong to the parcel. The prefetch states the parcel and every weighed line in the same
 * unit - the return method's - so the card keeps one weight unit for all of them, and picking
 * another one relabels the numbers rather than converting them.
 */
import { makeAutoObservable, toJS } from "mobx";
import { IOption } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { CP_DIMENSION_UNIT, WEIGHT_UNIT } from "../../../../return-methods/return-method-types";
import { IHeldShippingDetailsParcel, IShippingDetailsItem, IShippingDetailsParcel } from "../../rma-types";

const EMPTY_PARCEL: IHeldShippingDetailsParcel = {
    weight: null,
    weightUnits: null,
    length: null,
    width: null,
    height: null,
    distanceUnits: null,
};

const WEIGHT_UNIT_OPTIONS: IOption[] = [
    { value: WEIGHT_UNIT.GRAM, label: "Grams" },
    { value: WEIGHT_UNIT.KILOGRAM, label: "Kilograms" },
    { value: WEIGHT_UNIT.OUNCE, label: "Ounces" },
    { value: WEIGHT_UNIT.POUND, label: "Pounds" },
];

const DISTANCE_UNIT_OPTIONS: IOption[] = [
    { value: CP_DIMENSION_UNIT.CM, label: "Centimeters" },
    { value: CP_DIMENSION_UNIT.IN, label: "Inches" },
];

const isWeighed = (item: IShippingDetailsItem): boolean => item.weight !== null && item.weight !== undefined;

class ParcelStore {
    private _parcel: IHeldShippingDetailsParcel;
    private _items: IShippingDetailsItem[];
    private _itemWeightsRequired: boolean;

    /** Copied rather than held: the prefetch is not the card's to edit. */
    constructor(
        parcel: IShippingDetailsParcel | null,
        items: IShippingDetailsItem[] = [],
        itemWeightsRequired = false,
    ) {
        this._parcel = parcel ? { ...parcel } : { ...EMPTY_PARCEL };
        this._items = items.map((item) => ({ ...item }));
        this._itemWeightsRequired = itemWeightsRequired;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /* The parcel itself: what it is measured in, and how big it is. */

    public get weightUnitOptions(): IOption[] { return WEIGHT_UNIT_OPTIONS; }

    public get distanceUnitOptions(): IOption[] { return DISTANCE_UNIT_OPTIONS; }

    public get selectedWeightUnit(): IOption | null {
        return WEIGHT_UNIT_OPTIONS.find(({ value }) => value === this._parcel.weightUnits) ?? null;
    }

    public get selectedDistanceUnit(): IOption | null {
        return DISTANCE_UNIT_OPTIONS.find(({ value }) => value === this._parcel.distanceUnits) ?? null;
    }

    /** The sides read null when the prefetch left them out, as it does a method with no box. */
    public get length(): number | null { return this._parcel.length ?? null; }

    public get width(): number | null { return this._parcel.width ?? null; }

    public get height(): number | null { return this._parcel.height ?? null; }

    /**
     * Whether the parcel has a box. A box is all or none: once any side is filled, all three and
     * the unit are required; with none filled the parcel goes without one, as the prefetch sends a
     * method that ships no box of its own.
     */
    public get hasDimensions(): boolean {
        return [this.length, this.width, this.height].some((side) => side !== null);
    }

    /** Relabels the parcel and every weighed line with it: one unit for all of them. */
    public setWeightUnit(unit: WEIGHT_UNIT): void {
        this._parcel.weightUnits = unit;
        this._items
            .filter(isWeighed)
            .forEach((item) => { item.weightUnit = unit; });
    }

    public setDistanceUnit(unit: CP_DIMENSION_UNIT): void { this._parcel.distanceUnits = unit; }

    public setLength(length: number | null): void { this._parcel.length = length; }

    public setWidth(width: number | null): void { this._parcel.width = width; }

    public setHeight(height: number | null): void { this._parcel.height = height; }

    /* The weights: what the parcel weighed, and what each line inside it weighs. */

    /** What the parcel weighed on the carrier's scale, which is allowed to differ from its contents. */
    public get totalWeight(): number | null { return this._parcel.weight; }

    public setTotalWeight(weight: number | null): void { this._parcel.weight = weight; }

    /** The rows the card renders, as read; each is edited through setItemWeight. */
    public get items(): IShippingDetailsItem[] { return this._items; }

    /**
     * False when the aggregator takes no per-item weights: the prefetch may then send a line with
     * no weight at all, and the merchant may leave it so.
     */
    public get itemWeightsRequired(): boolean { return this._itemWeightsRequired; }

    /**
     * By product case rather than by position: the row a field belongs to is a fact, not an index.
     * A weighed line is stated in the parcel's unit - the backend refuses a weight without one - and
     * a cleared line carries none, as the prefetch sends a line it has no weight for.
     */
    public setItemWeight(productCaseId: string, weight: number | null): void {
        const item = this._items.find((row) => row.product_case_id === productCaseId);

        if (!item) {
            return;
        }

        item.weight = weight;

        if (weight === null) {
            delete item.weightUnit;
        } else {
            item.weightUnit = this._parcel.weightUnits;
        }
    }

    /* What goes back to the quote and the approve: copies, so a later edit cannot reach a sent body. */

    /**
     * The parcel as it stands. The distance unit of a parcel without a box goes back null: the box is
     * sent whole or not at all, and a unit with no sides is a part of one, which the backend refuses.
     */
    public get parcelRequest(): IHeldShippingDetailsParcel {
        const parcel = toJS(this._parcel);

        if (!this.hasDimensions && parcel.distanceUnits) {
            parcel.distanceUnits = null;
        }

        return parcel;
    }

    /** Every line as it stands, weighed or not, with every field it was read with. */
    public get itemsRequest(): IShippingDetailsItem[] { return toJS(this._items); }
}
export default ParcelStore;
